import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CustomerType } from '@today/types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { type CustomerCapabilities, customerCapabilities } from '@/auth/customer-capabilities'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { type CustomerDTO, customerService } from '@/services/customer.service'
import { errorMessage, statusOf, type Translate, unitOptions } from './customer-ui'

export const CUSTOMER_TABS = [
	'profile',
	'contactPoints',
	'assignment',
	'notes',
	'consents',
	'attributes',
	'activity',
] as const
export type CustomerTab = (typeof CUSTOMER_TABS)[number]

type Dialog = 'reassign' | 'convert' | 'anonymize' | null

/** Header props: each action only when the viewer may perform it on a non-anonymised customer. */
export const buildHeader = (
	t: Translate,
	data: CustomerDTO,
	caps: CustomerCapabilities,
	open: (dialog: Dialog) => void,
	edit: () => void,
) => {
	const mutable = !data.anonymizedAt
	return {
		t,
		fullName: data.fullName,
		anonymized: !mutable,
		type: data.type,
		status: data.status,
		externalId: data.externalId,
		source: data.source,
		onEdit: mutable && caps.write ? edit : undefined,
		onReassign: mutable && caps.assign ? () => open('reassign') : undefined,
		onConvert: mutable && caps.write && data.type === 'lead' ? () => open('convert') : undefined,
		onAnonymize: mutable && caps.anonymize ? () => open('anonymize') : undefined,
	}
}

/** Picker data for the reassign dialog, loaded only while it is open. */
function useReassignPickers(isOpen: boolean, tenantSlug: string | null) {
	const units = useQuery({ queryKey: ['org-units'], queryFn: customerService.orgUnits, enabled: isOpen })
	const users = useQuery({
		queryKey: ['tenant-users', tenantSlug],
		queryFn: () => customerService.users(tenantSlug ?? ''),
		enabled: isOpen && !!tenantSlug,
	})
	return {
		orgUnits: unitOptions(units.data ?? []),
		users: (users.data ?? []).map((u) => ({ id: u.userId, label: u.displayName })),
	}
}

/** Runs a dialog action; on success refreshes the customer + tabs, on failure yields a translated error. */
function useDialogMutation(id: string, onDone: () => void, onError: (error: unknown) => void) {
	const client = useQueryClient()
	return useMutation({
		mutationFn: (action: () => Promise<unknown>) => action(),
		onSuccess: async () => {
			onDone()
			await client.invalidateQueries({ queryKey: ['customer', id] })
			await client.invalidateQueries({ queryKey: ['customer-tab', id] })
		},
		onError,
	})
}

/** The reassign / convert / anonymise dialogs of one customer, and the mutation behind them. */
function useCustomerDialogs(id: string, tenantSlug: string | null, t: Translate, exists: (key: string) => boolean) {
	const [dialog, setDialog] = useState<Dialog>(null)
	const [reassign, setReassign] = useState({ orgUnitId: '', userId: '', reason: 'manual_reassign' })
	const [typed, setTyped] = useState('')
	const [error, setError] = useState<string | null>(null)
	const pickers = useReassignPickers(dialog === 'reassign', tenantSlug)
	const close = () => {
		setDialog(null)
		setError(null)
		setTyped('')
	}
	const mutation = useDialogMutation(id, close, (e) => setError(errorMessage(e, t, exists)))
	const body = { org_unit_id: reassign.orgUnitId, assigned_user_id: reassign.userId, reason: reassign.reason }
	return {
		open: setDialog,
		reassign: {
			t,
			isOpen: dialog === 'reassign',
			...pickers,
			values: reassign,
			error,
			onChange: (field: 'orgUnitId' | 'userId' | 'reason', value: string) =>
				setReassign({ ...reassign, [field]: value }),
			onConfirm: () => mutation.mutate(() => customerService.reassign(id, body)),
			onClose: close,
		},
		convert: {
			t,
			isOpen: dialog === 'convert',
			onConfirm: () => mutation.mutate(() => customerService.update(id, { type: CustomerType.CUSTOMER })),
			onClose: close,
		},
		anonymize: {
			t,
			isOpen: dialog === 'anonymize',
			typed,
			onTypedChange: setTyped,
			onConfirm: () => mutation.mutate(() => customerService.anonymize(id)),
			onClose: close,
		},
	}
}

/**
 * Detail page state (TO-418 customer-ui REQ-004/REQ-007): the customer, the
 * URL-addressed tab, the permission-gated header actions and the three dialogs.
 */
export function useCustomerDetail() {
	const { t, i18n } = useTranslation()
	const { id = '' } = useParams()
	const navigate = useNavigate()
	const [params, setParams] = useSearchParams()
	const { permissions, tenantSlug } = useViewerPermissions()
	const caps = customerCapabilities(permissions)
	const customer = useQuery({ queryKey: ['customer', id], queryFn: () => customerService.get(id) })
	const dialogs = useCustomerDialogs(id, tenantSlug, t, (key) => i18n.exists(key))
	const requested = params.get('tab') ?? ''
	const tab = (CUSTOMER_TABS as readonly string[]).includes(requested) ? (requested as CustomerTab) : 'profile'
	const data = customer.data
	return {
		t,
		id,
		caps,
		customer: data,
		notFound: statusOf(customer.error) === 404,
		tab,
		onTabChange: (next: string) => setParams(next === 'profile' ? {} : { tab: next }),
		header: data ? buildHeader(t, data, caps, dialogs.open, () => void navigate(`/customers/${id}/edit`)) : undefined,
		reassignDialog: dialogs.reassign,
		convertDialog: dialogs.convert,
		anonymizeDialog: { ...dialogs.anonymize, fullName: data?.fullName ?? '' },
	}
}
